import {
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useStore } from 'react-admin';
import { useStoreContext } from 'ra-core';
import {
    getResourceSignedUrl,
    ResourceId,
    ResourceSignedUrl,
    Url,
} from '@wwf/sdk';

import { isResourceUrlReachable } from './resourceUrlReachability';

const DEFAULT_EXPIRES_IN_SECONDS = 60 * 60 * 24 * 7; // 7 days

const cacheKey = (id: ResourceId | undefined | null) =>
    `resourceIdCache.${id ?? '__none__'}`;

const isExpired = (signed: ResourceSignedUrl): boolean =>
    new Date(signed.expiredAt) < new Date();

const getValidCached = (
    stored: ResourceSignedUrl | undefined
): ResourceSignedUrl | undefined => {
    if (!stored?.url) {
        return undefined;
    }
    if (isExpired(stored)) {
        return undefined;
    }
    return stored;
};

const staleTimeMs = (signed: ResourceSignedUrl | undefined): number => {
    if (!signed) {
        return 0;
    }
    return Math.max(0, new Date(signed.expiredAt).getTime() - Date.now());
};

const clearResourceCache = (
    key: string,
    setItem: (key: string, value: ResourceSignedUrl | undefined) => void,
    setStoredResourceUrl: (value: ResourceSignedUrl | undefined) => void
) => {
    setItem(key, undefined);
    setStoredResourceUrl(undefined);
};

export const resourceSignedUrlQueryKey = (id: ResourceId | undefined | null) =>
    ['resourceSignedUrl', id ?? ''] as const;

/** Call from onError handlers when the browser fails to load the signed URL (any media type). */
export const useResourceLoadFailureHandler = (
    id: ResourceId | undefined | null
): (() => void) => {
    const key = cacheKey(id);
    const { setItem } = useStoreContext();
    const [, setStoredResourceUrl] = useStore<ResourceSignedUrl | undefined>(
        key,
        undefined
    );
    const queryClient = useQueryClient();

    return useCallback(() => {
        if (!id) {
            return;
        }
        clearResourceCache(key, setItem, setStoredResourceUrl);
        void queryClient.invalidateQueries({
            queryKey: resourceSignedUrlQueryKey(id),
        });
    }, [id, key, queryClient, setItem, setStoredResourceUrl]);
};

export const useResource = (
    id: ResourceId | undefined | null
): UseQueryResult<Url | null, Error> => {
    const key = cacheKey(id);
    const { getItem, setItem } = useStoreContext();
    const [storedResourceUrl, setStoredResourceUrl] = useStore<
        ResourceSignedUrl | undefined
    >(key, undefined);

    const cached = getValidCached(storedResourceUrl);

    return useQuery({
        queryKey: resourceSignedUrlQueryKey(id),
        enabled: !!id,
        retry: false,
        refetchOnWindowFocus: false,
        initialData: cached?.url,
        staleTime: staleTimeMs(cached),
        refetchOnMount: cached ? 'always' : true,
        queryFn: async (): Promise<Url | null> => {
            if (!id) {
                return null;
            }

            const stored = getItem<ResourceSignedUrl>(key, undefined);
            if (stored?.url && isExpired(stored)) {
                clearResourceCache(key, setItem, setStoredResourceUrl);
            }

            const fromStore = getValidCached(
                getItem<ResourceSignedUrl>(key, undefined)
            );
            if (fromStore) {
                const reachable = await isResourceUrlReachable(fromStore.url);
                if (reachable) {
                    return fromStore.url;
                }
                clearResourceCache(key, setItem, setStoredResourceUrl);
            }

            const result = await getResourceSignedUrl(
                id,
                DEFAULT_EXPIRES_IN_SECONDS
            );
            setStoredResourceUrl(result);
            return result.url;
        },
    });
};
